/**
 * Pachisi Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum PachisiErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface PachisiError {
  type: PachisiErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class PachisiErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: PachisiError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: PachisiErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): PachisiError {
    const error: PachisiError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Pachisi', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: PachisiErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case PachisiErrorType.INVALID_MOVE:
      case PachisiErrorType.NOT_YOUR_TURN:
      case PachisiErrorType.GAME_ENDED:
      case PachisiErrorType.GAME_LOGIC:
      case PachisiErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case PachisiErrorType.CONNECTION_ERROR:
      case PachisiErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case PachisiErrorType.CORRUPTED_DATA:
      case PachisiErrorType.SAVE_ERROR:
      case PachisiErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): PachisiError {
    return this.handleError(
      PachisiErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleNotYourTurn(): PachisiError {
    return this.handleError(
      PachisiErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): PachisiError {
    return this.handleError(
      PachisiErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): PachisiError {
    return this.handleError(
      PachisiErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): PachisiError {
    return this.handleError(
      PachisiErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): PachisiError {
    return this.handleError(
      PachisiErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: PachisiErrorType): boolean {
    switch (type) {
      case PachisiErrorType.INVALID_MOVE:
      case PachisiErrorType.NOT_YOUR_TURN:
      case PachisiErrorType.GAME_ENDED:
      case PachisiErrorType.GAME_LOGIC:
      case PachisiErrorType.CONNECTION_ERROR:
      case PachisiErrorType.CORRUPTED_DATA:
      case PachisiErrorType.AI_TIMEOUT:
      case PachisiErrorType.SAVE_ERROR:
      case PachisiErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: PachisiError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: PachisiError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Pachisi Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): PachisiError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
